import { ZodSchema, ZodType } from 'zod';
import { useContext, createContext, FC, PropsWithChildren, SyntheticEvent } from 'react';
import noop from 'utils/noop';
import { useQuerySchema } from '../schemas';
import { CompleteQuery } from '../types';
import { useOnSubmit } from '../hooks/useOnSubmit';
import { useQueryErrorStateContext } from './queryErrorState';
import { aggregatePathError } from '../utils/aggregatePathError';

export type QueryValidationContextProps = {
  formId: string;
  onSubmitSuccess: (
    query: CompleteQuery,
    event?: SyntheticEvent<HTMLFormElement, SubmitEvent>,
  ) => void;
  onSubmitError?: () => void;
  schema?: ZodSchema<CompleteQuery>;
};

export const defaultQueryValidationState = {
  pathErrors: [],
  getHasErrorFromFieldPath: () => false,
  resetErrorsFromFieldPath: noop,
};

export const QueryValidationContext = createContext({});
export const useQueryValidationContext = () => useContext(QueryValidationContext);

export const QueryValidationContextProvider: FC<PropsWithChildren<QueryValidationContextProps>> = ({
  children,
  formId,
  onSubmitSuccess,
  onSubmitError,
  schema,
}) => {
  const defaultQuerySchema = useQuerySchema();
  const { setPathErrors } = useQueryErrorStateContext();

  const onSubmit = useOnSubmit({
    schema: (schema ?? defaultQuerySchema) as ZodType<CompleteQuery>,
    onSuccessCb: ({ parsedQuery, submitEvent }) => {
      onSubmitSuccess(parsedQuery, submitEvent);
      setPathErrors([]);
    },
    onErrorCb: ({ errors }) => {
      const pathErrorsFromSchema = errors.flatMap(aggregatePathError);
      setPathErrors(pathErrorsFromSchema);
      if (onSubmitError) onSubmitError();
    },
  });

  return (
    <QueryValidationContext.Provider value={{}}>
      <form data-test={formId} id={formId} onSubmit={onSubmit}>
        {children}
      </form>
    </QueryValidationContext.Provider>
  );
};
